import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { AppContext } from '@com/core/common/contexts/appContext';
import { baseConfig } from '@com/core/common/baseConfig';
import { Transition } from 'react-transition-group';
import { colors } from '@com/core/noon/styles/noonTheme';
import Overlay from '@com/core/common/components/Overlay';
import { CartContext } from '../contexts/cartContext';
import Loader from './Loader';

type TOption = {
    name: string;
    id: number;
};

type TProps = {
    children: React.ReactNode;
    options?: TOption[];
    selected?: TOption;
    handleOptionChange?: (optionId: number) => void;
    isSearchable?: boolean;
    searchLabel?: string;
    searchPlaceholder?: string;
    noSearchText?: string;
    renderOptions?: React.ReactElement | null;
    sliderId?: string;
    sizingGuide?: string;
    width?: number;
    padding?: string;
};

const SlidingOptionsPanel = ({
    children,
    handleOptionChange: xHandleOptionChange,
    isSearchable = false,
    noSearchText,
    options = [],
    searchLabel,
    searchPlaceholder,
    selected,
    renderOptions = null,
    sliderId = 'citySwitcher',
    width = 320,
    padding = '10px 0',
    // TODO @Harshal - manage this in Drawer component
    sizingGuide,
}: TProps): JSX.Element => {
    const { isSlidingOptionsPanelOpen, toggleSlidingOptionsPanel } = useContext(AppContext);
    const { isCityLoading, setDestination } = useContext(CartContext);
    const [search, setSearch] = useState('');
    const [selectCity, setSelectCity] = useState(0);

    const toggleOptionsPanel = (): void => {
        toggleSlidingOptionsPanel(sliderId);
    };
    const handleOptionChange =
        xHandleOptionChange ||
        (async (id: number): Promise<void> => {
            setSelectCity(id);
            await setDestination(id);
            toggleSlidingOptionsPanel(sliderId);
            setSelectCity(0);
        });

    const handleSearch = ({ target }: { target: HTMLInputElement }): void => setSearch(target.value);

    let filteredOptions = options;
    if (isSearchable && search) {
        filteredOptions = options.filter(option => option.name.toLowerCase().includes(search.toLowerCase()));
    }
    const { radioOff, radioOn, searchDark } = baseConfig.assets.icons;

    const OpacityDuration = 200;

    const defaultStyle = {
        transition: `opacity ${OpacityDuration}ms ease-in-out`,
        zIndex: 99,
        opacity: 0,
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    };

    const transitionStyles = {
        entering: { opacity: 0 },
        entered: { opacity: 1 },
        exiting: { opacity: 0 },
        exited: { opacity: 0 },
    };

    return (
        <>
            <Trigger onClick={toggleOptionsPanel}>{children}</Trigger>

            {/* <------= Stopped Background Scrolling =------> */}
            <Transition in={isSlidingOptionsPanelOpen[sliderId]} timeout={OpacityDuration} unmountOnExit>
                {state => (
                    <div
                        style={{
                            ...defaultStyle,
                            ...transitionStyles[state],
                        }}>
                        <Overlay
                            isPanelOpen={isSlidingOptionsPanelOpen[sliderId]}
                            isClicked={toggleOptionsPanel}
                            type="blur"
                        />
                    </div>
                )}
            </Transition>

            {sizingGuide ? (
                <Panel
                    isPanelOpen={isSlidingOptionsPanelOpen[sliderId]}
                    dangerouslySetInnerHTML={{ __html: sizingGuide }}
                    panelWidth={width}
                    paddingSize={padding}
                />
            ) : (
                <Panel isPanelOpen={isSlidingOptionsPanelOpen[sliderId]} panelWidth={width} paddingSize={padding}>
                    {isSearchable && (
                        <SearchBox>
                            <SearchLabel>
                                {searchLabel}
                                <button onClick={toggleOptionsPanel} type="button">
                                    <img src={baseConfig.assets.icons.x} alt="cross" />
                                </button>
                            </SearchLabel>
                            <SearchInputWrapper>
                                <SearchInput type="text" placeholder={searchPlaceholder} onChange={handleSearch} />
                                <img src={searchDark} alt="search" />
                            </SearchInputWrapper>
                        </SearchBox>
                    )}
                    {renderOptions}
                    {isSearchable &&
                        (!renderOptions && filteredOptions.length ? (
                            <List isSearchable={isSearchable}>
                                {filteredOptions.map(option => {
                                    const isSelected = option.id === (selected && selected.id);
                                    return (
                                        <ListOption
                                            key={option.id}
                                            isSelected={isSelected}
                                            onClick={() => handleOptionChange(option.id)}>
                                            {option.name}
                                            {isCityLoading && selectCity === option.id && (
                                                <Loader type="bounce" color={colors.brandSecondary} />
                                            )}
                                            <img src={isSelected ? radioOn : radioOff} alt="selected" />
                                        </ListOption>
                                    );
                                })}
                            </List>
                        ) : (
                            <NoSearch>{noSearchText}</NoSearch>
                        ))}
                </Panel>
            )}
        </>
    );
};

const Trigger = styled.div`
    cursor: pointer;
`;

const Panel = styled.div<{ isPanelOpen: boolean; panelWidth: number; paddingSize: string }>`
    position: fixed;
    top: 0;
    bottom: 0;
    width: ${({ panelWidth }): string => `${panelWidth}px`};
    padding: ${({ paddingSize }): string => `${paddingSize}`};
    background-color: ${colors.bgPrimary};
    z-index: 99;
    transition: right ease-in-out 0.4s;
    overflow-y: auto;
    ${({ isPanelOpen }): string => `right: ${isPanelOpen ? 0 : '-100%'}`};
`;

const SearchBox = styled.div`
    background-color: ${colors.bgTertiary};
    display: flex;
    flex-direction: column;
    padding: 10px;
    margin-top: -10px;
    font-size: 1.25rem;
`;

const SearchLabel = styled.span`
    display: flex;
    justify-content: space-between;
    font-weight: 700;
    font-size: 1.3333rem;
`;

const SearchInputWrapper = styled.div`
    display: flex;
    box-sizing: border-box;
    align-items: center;
    border: 1px solid ${colors.borderDark};
    background-color: ${colors.bgPrimary};
    padding: 10px;
    margin-top: 10px;
`;

const SearchInput = styled.input`
    flex: 1;
    border: unset;
    margin-right: 15px;
    font-weight: normal;
`;

// HERE 102 PX is the height of the search box
const List = styled.ul<{ isSearchable: boolean }>`
    overflow-y: auto;
    cursor: pointer;
    ${({ isSearchable }): string => `height: ${isSearchable ? `calc(100% - 102px)` : '100%'}`};
`;

const ListOption = styled.li<{ isSelected: boolean }>`
    display: flex;
    font-size: 1.08333rem;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    box-sizing: border-box;
    color: ${colors.bodyMid};
    position: relative;
    ${({ isSelected }): string =>
        `color: ${isSelected ? colors.body : colors.bodyMid}; font-weight: ${isSelected ? '600' : 'normal'}`};
    &:hover {
        background-color: ${colors.bgTertiary};
    }
`;

const NoSearch = styled.span`
    display: flex;
    width: 100%;
    font-size: 1.08333rem;
    padding: 15px;
    box-sizing: border-box;
    color: ${colors.bodyMid};
    font-weight: normal;
`;

export default SlidingOptionsPanel;
